import { Injectable, Inject } from '@angular/core';
import { ENV_CONFIG } from 'src/app/core/injection.token';
import { ApiService } from 'src/app/core/services/api.service';

@Injectable()
export class ContractAPIService {
  public constructor(
    @Inject(ApiService) private _apiService: ApiService,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvData
  ) {}

  private readonly controller = 'contract';

  public getPagedContracts(page: number, filter: app.models.ContractsFilterModel) {
    const request = 'getPagedContracts';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    const data = {
      isActive: filter.activeContracts,
      terminationUntilDate: filter.terminationUntilDate,
      contractTypeCodes: filter.contractTypeCodes,
      contractAssetIds: filter.contractAssetIds,
      isPaid: filter.isPaid,
      isNotPaid: filter.isNotPaid,
      isOverdue: filter.isOverdue,
      searchText: filter.searchText,
      page: page,
    };
    return this._apiService.postGet<app.models.DynamicResponse<app.models.PagedResult<app.models.ContractModel>>>(
      url,
      data
    );
  }

  public getPagedAdminContracts(page: number, filter: app.models.ContractsFilterModel) {
    const request = 'getPagedAdminContracts';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    const data = {
      isActive: filter.activeContracts,
      terminationUntilDate: filter.terminationUntilDate,
      contractTypeCodes: filter.contractTypeCodes,
      contractAssetIds: filter.contractAssetIds,
      isPaid: filter.isPaid,
      isNotPaid: filter.isNotPaid,
      isOverdue: filter.isOverdue,
      partnerExternalEntityId: filter.partnerExternalEntityId,
      page: page,
    };
    return this._apiService.postGet<app.models.DynamicResponse<app.models.PagedResult<app.models.ContractModel>>>(
      url,
      data
    );
  }

  public getContract(contractId: string) {
    const request = 'getContract';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      contractId: contractId,
    });

    return this._apiService.get<app.models.ContractDetailModel>(url);
  }

  public getContractAssets(
    contractId?: string,
    fromActiveContracts?: boolean,
    partnerExternalEntityId: string | undefined = undefined
  ) {
    const request = 'getContractAssets';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      contractId: contractId,
      fromActiveContracts: fromActiveContracts,
      partnerExternalEntityId: partnerExternalEntityId,
    });

    return this._apiService.get<app.models.DynamicResponse<app.models.ContractDetailAssetModel[]>>(url);
  }

  public getContractPaymentSchedules(contractId: string) {
    const request = 'getContractPaymentSchedules';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      contractId: contractId,
    });

    return this._apiService.get<app.models.DynamicResponse<app.models.ContractDetailPaymentScheduleModel[]>>(url);
  }

  public getContractCollaterals(contractId: string) {
    const request = 'getContractCollaterals';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      contractId: contractId,
    });

    return this._apiService.get<app.models.DynamicResponse<app.models.ContractDetailCollateralModel[]>>(url);
  }

  public getContractInsurances(contractId: string) {
    const request = 'getContractInsurances';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      contractId: contractId,
    });

    return this._apiService.get<app.models.DynamicResponse<app.models.ContractDetailInsuranceModel[]>>(url);
  }

  public getActiveContracts() {
    const request = 'getActiveContracts';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.ActiveContractModel[]>(url);
  }

  public getInactiveContracts() {
    const request = 'getInactiveContracts';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.ActiveContractModel[]>(url);
  }

  public getDashboardContracts() {
    const request = 'getDashboardContracts';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.dashboard.ContractModel[]>(url);
  }

  public getContractInsurancesLite(
    contractExternalEntityId: string,
    assetExternalEntityId: string,
    portalCodes: string[]
  ) {
    const request = 'getContractInsurancesLite';
    const body = {
      contractExternalEntityId: contractExternalEntityId,
      assetExternalEntityId: assetExternalEntityId,
      portalCodes: portalCodes,
    };
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.postGet<app.models.ContractInsurancesLiteModel>(url, body);
  }
}
